import { useMemo, useState } from 'react';

/**
 * Manages show/hide state for a table's columns (the new-UI equivalent of the legacy
 * `tableControlParams` column picker). Returns the currently-visible columns plus the props for
 * <ColumnPicker/>.
 *
 * Toggleable = columns with a `label` and not `locked`. Action columns (no label, e.g. the select
 * checkbox / row actions) are always shown. The last visible toggleable column can't be hidden, so
 * the table never collapses to nothing. State is in-memory (resets on remount), matching the legacy
 * behaviour.
 */
const useColumnVisibility = (columns) => {
  const [hidden, setHidden] = useState(() => new Set());

  const toggleable = useMemo(() => columns.filter((col) => col.label && !col.locked), [columns]);

  const visibleColumns = useMemo(() => columns.filter((col) => !hidden.has(col.key)), [columns, hidden]);

  const visibleToggleableCount = toggleable.filter((col) => !hidden.has(col.key)).length;

  const pickerItems = useMemo(
    () =>
      toggleable.map((col) => {
        const visible = !hidden.has(col.key);
        return {
          key: col.key,
          label: col.label,
          visible,
          // Don't let the user hide the last remaining toggleable column.
          disabled: visible && visibleToggleableCount <= 1,
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toggleable, hidden, visibleToggleableCount],
  );

  const toggle = (key) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        return next;
      }
      // Keep at least one toggleable column visible.
      if (visibleToggleableCount <= 1) return prev;
      next.add(key);
      return next;
    });
  };

  // Only worth showing the control when there's more than one column to toggle.
  const hasPicker = toggleable.length > 1;

  return { visibleColumns, pickerItems, toggle, hasPicker };
};

export default useColumnVisibility;
